import { Send } from 'lucide-react';

import { cn } from '@/nucleo/utilitarios';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';

import type { DreEmDefasagem } from '../contrato';

/**
 * A linha de uma DRE parada.
 *
 * As duas listas do painel — "Cobranças DRE no mês" e "DREs em defasagem" —
 * mostram a mesma coisa com tom diferente: a mesma diretoria, os mesmos lotes
 * parados, a mesma cobrança. Duas repetições já pedem extração (CLAUDE.md,
 * "Regras de interface", item 1), e manter uma linha só evita que as duas
 * descrevam a mesma DRE com frases diferentes na mesma tela.
 */

export type TomDaLinha = 'atencao' | 'critico';

interface LinhaDeDreProps {
  dre: DreEmDefasagem;
  tom: TomDaLinha;
  /** `false` esconde a cobrança — quem não pode cobrar não vê o botão. */
  podeCobrar: boolean;
  aoCobrar: (dre: DreEmDefasagem) => void;
}

export function LinhaDeDre({ dre, tom, podeCobrar, aoCobrar }: LinhaDeDreProps) {
  const cobrancas =
    dre.cobrancasDisparadas === 0
      ? 'sem cobrança'
      : `${dre.cobrancasDisparadas} cobrança(s) enviada(s)`;

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-xl border p-3 sm:flex-row sm:items-center sm:justify-between',
        tom === 'critico'
          ? 'border-destructive/40 bg-destructive/5'
          : 'border-warning/50 bg-warning/5',
      )}
    >
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-2 text-sm font-semibold">
          <span>
            {dre.dreCodigo} — {dre.dreNome}
          </span>
          {tom === 'critico' && (
            <Badge variant="destructive" className="text-[10px] uppercase tracking-wide">
              crítico
            </Badge>
          )}
        </p>
        <p className="text-xs text-muted-foreground">
          {dre.lotesParados} lote(s) parado(s) há até {dre.diasParadoMaximo} dia(s) · {cobrancas}
        </p>
      </div>
      {podeCobrar && (
        <Button
          variant={tom === 'critico' ? 'destructive' : 'outline'}
          size="sm"
          className="w-full sm:w-auto"
          onClick={() => aoCobrar(dre)}
        >
          <Send className="mr-1.5 h-4 w-4" aria-hidden="true" />
          Cobrar
        </Button>
      )}
    </div>
  );
}
